// A small, local filter engine for the album grid: every criterion maps a listed file to one value,
// and the user picks any number of values per criterion. A file is shown when, for every criterion
// with picked values, its value is one of them (OR within a criterion, AND across criteria).
// New criteria only need an entry in FILE_FILTER_CRITERIA.

import {DriveFile} from '../drive/types';
import {aspectRatioOf, compareAspectRatios, formatAspectRatio} from './aspectRatio';
import {parseFileName} from './naming';

/** The value of files a criterion can't classify (e.g. a name outside the convention, no dimensions). */
export const UNKNOWN_FILTER_VALUE = '';

export interface FileFilterCriterion {
    id: string;
    label: string;
    /** The file's value for this criterion; `null` when it has none (it then counts as "unknown"). */
    valueOf: (file: DriveFile) => string | null;
    /** How a value is shown; defaults to the value itself. */
    formatValue?: (value: string) => string;
    /** Orders the offered values; defaults to alphabetical. "Unknown" always comes last. */
    compareValues?: (a: string, b: string) => number;
}

/** Picked values by criterion id; a criterion without picked values doesn't filter. */
export type FileFilterSelection = Readonly<Record<string, readonly string[]>>;

export interface FileFilterOption {
    value: string;
    label: string;
    /** Files with this value among those the *other* criteria let through. */
    count: number;
    selected: boolean;
}

export interface FileFilterFacet {
    criterion: FileFilterCriterion;
    options: FileFilterOption[];
}

const UNKNOWN_LABEL = 'Ismeretlen';

function valueKey(criterion: FileFilterCriterion, file: DriveFile): string {
    return criterion.valueOf(file) ?? UNKNOWN_FILTER_VALUE;
}

function activeCriteria(criteria: readonly FileFilterCriterion[], selection: FileFilterSelection) {
    return criteria
        .filter((c) => (selection[c.id]?.length ?? 0) > 0)
        .map((c) => ({criterion: c, picked: new Set(selection[c.id])}));
}

export function isFilterActive(selection: FileFilterSelection): boolean {
    return Object.values(selection).some((values) => values.length > 0);
}

/** How many values are picked across every criterion. */
export function countSelectedValues(selection: FileFilterSelection): number {
    return Object.values(selection).reduce((sum, values) => sum + values.length, 0);
}

/** The files matching the selection, in their original order. */
export function applyFileFilters(
    files: readonly DriveFile[],
    criteria: readonly FileFilterCriterion[],
    selection: FileFilterSelection,
): DriveFile[] {
    const active = activeCriteria(criteria, selection);
    if (active.length === 0) return [...files];
    return files.filter((file) => active.every(({criterion, picked}) => picked.has(valueKey(criterion, file))));
}

/**
 * The values each criterion offers, with faceted counts: a value's count is how many files would show
 * if it were picked alongside the other criteria's current picks. Picked values stay listed even when
 * no file has them any more, so they can be unpicked.
 */
export function fileFilterFacets(
    files: readonly DriveFile[],
    criteria: readonly FileFilterCriterion[],
    selection: FileFilterSelection,
): FileFilterFacet[] {
    const active = activeCriteria(criteria, selection);
    return criteria.map((criterion) => {
        const others = active.filter((a) => a.criterion !== criterion);
        const counts = new Map<string, number>();
        for (const file of files) {
            const key = valueKey(criterion, file);
            const passesOthers = others.every((o) => o.picked.has(valueKey(o.criterion, file)));
            counts.set(key, (counts.get(key) ?? 0) + (passesOthers ? 1 : 0));
        }
        const picked = selection[criterion.id] ?? [];
        for (const value of picked) {
            if (!counts.has(value)) counts.set(value, 0);
        }

        const compare = criterion.compareValues ?? ((a: string, b: string) => a.localeCompare(b, 'hu'));
        const options = Array.from(counts, ([value, count]) => ({
            value,
            label: value === UNKNOWN_FILTER_VALUE ? UNKNOWN_LABEL : (criterion.formatValue?.(value) ?? value),
            count,
            selected: picked.includes(value),
        }));
        options.sort((a, b) => {
            if (a.value === UNKNOWN_FILTER_VALUE || b.value === UNKNOWN_FILTER_VALUE) {
                return Number(a.value === UNKNOWN_FILTER_VALUE) - Number(b.value === UNKNOWN_FILTER_VALUE);
            }
            return compare(a.value, b.value);
        });
        return {criterion, options};
    });
}

/** The selection with one value of a criterion picked or unpicked. */
export function toggleFilterValue(selection: FileFilterSelection, criterionId: string, value: string): FileFilterSelection {
    const current = selection[criterionId] ?? [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    return {...selection, [criterionId]: next};
}

// --- Criteria ---

export const PHOTOGRAPHER_CRITERION: FileFilterCriterion = {
    id: 'photographer',
    label: 'Fotós',
    valueOf: (file) => parseFileName(file.name)?.photographerCode ?? null,
};

export const ASPECT_RATIO_CRITERION: FileFilterCriterion = {
    id: 'aspectRatio',
    label: 'Képarány',
    valueOf: aspectRatioOf,
    formatValue: formatAspectRatio,
    compareValues: compareAspectRatios,
};

/** The criteria the album grid offers, in the order they are shown. */
export const FILE_FILTER_CRITERIA: readonly FileFilterCriterion[] = [PHOTOGRAPHER_CRITERION, ASPECT_RATIO_CRITERION];
